import { useGLTF } from "@react-three/drei";
import { GroupProps } from "@react-three/fiber";
import * as THREE from "three";
import { useMemo } from "react";
import stairsGlbUrl from "../assets/stairs/Stairs.glb";

type StairsPivot = "center" | "corner";
export type StairsProps = GroupProps & {
  /**
   * Where the model's pivot/origin should be:
   * - "center": centered on X/Z, grounded on Y
   * - "corner" (default): bottom-min corner is at the origin (useful for "rotate around corner")
   */
  pivot?: StairsPivot;
  /**
   * Brighten the GLB materials.
   * - 1.0 = no change
   * - 1.1..1.4 = noticeably lighter
   */
  brightness?: number;
  /**
   * Extra "glow"/fill light baked into the material. 0 disables.
   */
  emissiveBoost?: number;
};

const STAIRS_PATH = stairsGlbUrl;

// Default: place under the gate (gate defaults to [0, 0, 0]).
const DEFAULT_STAIRS_POSITION: [number, number, number] = [-0.5, -24.8, 27.4];
const DEFAULT_STAIRS_SCALE = 0.2; // uniform
const DEFAULT_STAIRS_ROTATION: [number, number, number] = [0, Math.PI / 2, 0]; // 90deg around Y
const DEFAULT_STAIRS_PIVOT: StairsPivot = "corner";

export default function Stairs({
  pivot = DEFAULT_STAIRS_PIVOT,
  position = DEFAULT_STAIRS_POSITION,
  scale = DEFAULT_STAIRS_SCALE,
  rotation = DEFAULT_STAIRS_ROTATION,
  brightness: _brightness,
  emissiveBoost: _emissiveBoost,
  ...props
}: StairsProps) {
  const { scene } = useGLTF(STAIRS_PATH);

  const sceneClone = useMemo(() => {
    const cloned = scene.clone(true);

    cloned.traverse((obj) => {
      const mesh = obj as THREE.Mesh;
      if (!mesh.isMesh) return;
      const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      mats.forEach((mat, idx) => {
        if (!mat) return;
        const std = mat as THREE.MeshStandardMaterial;
        const upgraded = new THREE.MeshPhysicalMaterial({
          map: std.map ?? null,
          normalMap: std.normalMap ?? null,
          normalScale: std.normalScale?.clone() ?? new THREE.Vector2(1.6, 1.6),
          roughnessMap: std.roughnessMap ?? null,
          metalnessMap: std.metalnessMap ?? null,
          aoMap: std.aoMap ?? null,
          color: new THREE.Color("#e8e4dd"),
          emissive: new THREE.Color("#8d7449"),
          roughness: 0.8,
          metalness: 0.0,
          clearcoat: 0.18,
          clearcoatRoughness: 0.41,
          envMapIntensity: 3.0,
          sheen: 0.32,
          sheenColor: new THREE.Color("#7c6534"),
          sheenRoughness: 0.89,
        });
        if (Array.isArray(mesh.material)) {
          (mesh.material as THREE.Material[])[idx] = upgraded;
        } else {
          mesh.material = upgraded;
        }
      });
    });

    return cloned;
  }, [scene]);

  const pivotOffset = useMemo<[number, number, number]>(() => {
    const box = new THREE.Box3().setFromObject(sceneClone);
    if (!Number.isFinite(box.min.x) || !Number.isFinite(box.max.x)) {
      return [0, 0, 0];
    }

    if (pivot === "corner") {
      // Bottom-min corner at origin.
      return [-box.min.x, -box.min.y, -box.min.z];
    }

    // Centered on X/Z, grounded on Y.
    const center = box.getCenter(new THREE.Vector3());
    return [-center.x, -box.min.y, -center.z];
  }, [pivot, sceneClone]);

  return (
    <group position={position} scale={scale} rotation={rotation} {...props}>
      <primitive object={sceneClone} position={pivotOffset} />
    </group>
  );
}

useGLTF.preload(STAIRS_PATH);
